import { isRecord } from '~/utils/isRecord';

/** svc-lux-loyalty's decision group for Société "choose one" campaigns. */
export const CHOOSE_ONE_CAMPAIGN_GROUP = 'CHOOSE_ONE_CAMPAIGN';

interface ChoiceCampaignCatalog {
  campaignName: string;
  choiceLabel: string;
}

/**
 * Self-enrol catalogs of Société choice campaigns, keyed by catalog id. svc-lux-loyalty seeds these with fixed ids
 * in its migrations, so they are the same in every environment. Add a campaign's catalogs here when it launches.
 */
const CHOICE_CAMPAIGN_CATALOGS: Record<string, ChoiceCampaignCatalog> = {
  // CHOICE_CAMPAIGN_NOV_2026_DOUBLE_POINTS
  '9ed9951f-b7ac-46a7-9408-2cdaa723b9c3': { campaignName: 'Choose Your Double Rewards', choiceLabel: 'Double Points' },
  // CHOICE_CAMPAIGN_NOV_2026_DOUBLE_STATUS_CREDITS
  '02e4cc6b-86a7-4585-ba28-5a84b15ef192': {
    campaignName: 'Choose Your Double Rewards',
    choiceLabel: 'Double Status Credits',
  },
};

export interface ChoiceCampaignSelection {
  catalogId: string;
  campaignName: string;
  choiceLabel: string;
}

/**
 * The customer's choices in Société choice campaigns. The account read only returns groups whose catalogs are
 * active today, so an ended campaign drops out on its own. A customer who hasn't chosen yet has no selection.
 */
export function getChoiceCampaignSelections(
  earnDecisions: Array<App.LuxLoyaltyEarnDecisionGroupSummary> | undefined,
): Array<ChoiceCampaignSelection> {
  return (earnDecisions ?? [])
    .filter((decision) => decision.group === CHOOSE_ONE_CAMPAIGN_GROUP && decision.status === 'RESOLVED')
    .flatMap((decision) => decision.selectedCatalogIds)
    .map((catalogId) => ({
      catalogId,
      // An unknown catalog is a newer campaign this list doesn't name yet: still show that a choice was made.
      ...(CHOICE_CAMPAIGN_CATALOGS[catalogId] ?? { campaignName: 'Société choice campaign', choiceLabel: catalogId }),
    }));
}

/**
 * Reads `result.earnDecisions` from an account response. The published contract doesn't type it on every version,
 * so anything malformed is dropped rather than trusted.
 */
export function parseEarnDecisions(value: unknown): Array<App.LuxLoyaltyEarnDecisionGroupSummary> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (decision): decision is App.LuxLoyaltyEarnDecisionGroupSummary =>
      isRecord(decision) &&
      typeof decision.group === 'string' &&
      typeof decision.status === 'string' &&
      Array.isArray(decision.selectedCatalogIds) &&
      decision.selectedCatalogIds.every((id) => typeof id === 'string'),
  );
}
